import { css } from "@emotion/react";
import { gql, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AUTH_URL, getUrl, zitadelRoutes } from "../../../includes/routes";
import { FAILED_AUTHENTICATION_PARAM } from "../../../includes/constants";
import { FLAG_ZITADEL_MFA } from "../../../includes/featureFlags";
import { hasFeatureFlag } from "../../../includes/site";
import { useAppState } from "../../../contexts/AppContext";
import { useZitadel, ZitadelSession, ZitadelTotpRegistration } from "../../../contexts/ZitadelContext";
import Button from "../../Button";
import { API_BASE_URL } from "../../../../config/config";
import Loading from "../../utils/Loading";

type ZitadelStartTotpRegistrationMutationResponse = {
    zitadelStartTotpRegistration: {
        session: ZitadelSession;
        secret: ZitadelTotpRegistration;
    };
}

type ZitadelStartTotpRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelSkipMfaRegistrationMutationResponse = {
    zitadelSkipMfaRegistration: {
        id: string;
    };
}

type ZitadelSkipMfaRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

const MfaInitialisation = () => {
    const navigate = useNavigate();

    const [isTotpLoading, setIsTotpLoading] = useState(false);
    const [isOtpEmailLoading, setIsOtpEmailLoading] = useState(false);
    const [isSkipLoading, setIsSkipLoading] = useState(false);
    const [isFallbackRedirectLoading, setIsFallbackRedirectLoading] = useState(false);

    const [appState] = useAppState();
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;

    const {
        loginInfo,
        setLoginInfo,
        clearLoginInfo,
        session,
        setSession,
        clearSession,
        setTotpRegistration,
        clearTotpRegistration,
    } = useZitadel();

    const hasZitadelMfaFlag = hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_MFA);

    const [zitadelStartTotpRegistration] =
        useMutation<ZitadelStartTotpRegistrationMutationResponse, ZitadelStartTotpRegistrationMutationVariables>(
            ZITADEL_START_TOTP_REGISTRATION,
        );

    const [zitadelSkipMfaRegistration] =
        useMutation<ZitadelSkipMfaRegistrationMutationResponse, ZitadelSkipMfaRegistrationMutationVariables>(
            ZITADEL_SKIP_MFA_REGISTRATION,
        );

    const onSelectTotp = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register authenticator app.");

        setIsTotpLoading(true);

        zitadelStartTotpRegistration({
            variables: {
                sessionId: session?.id,
                sessionToken: session?.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("TOTP registration did not return a valid response.");

            setLoginInfo(prev => prev ? { ...prev, isTotpChallengeRequired: true } : null);
            setSession(data.zitadelStartTotpRegistration.session);
            setTotpRegistration(data.zitadelStartTotpRegistration.secret);

            navigate(getUrl(zitadelRoutes.TOTP_CHALLENGE.route));
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }

    const onSelectOtpEmail = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register email authentication.");

        setIsOtpEmailLoading(true);

        setLoginInfo(prev => prev ? { ...prev, isOtpEmailChallengeRequired: true } : null);
        navigate(getUrl(zitadelRoutes.OTP_EMAIL_CHALLENGE.route));
    }

    const onSelectSkip = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to skip MFA registration.");

        setIsSkipLoading(true);

        zitadelSkipMfaRegistration({
            variables: {
                sessionId: session?.id,
                sessionToken: session?.token,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            window.location.href = `${API_BASE_URL}/auth/login-redirect`;
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }

    useEffect(function fallbackRedirection() {
        /** SKip fallback redirection if actions are loading since the actions do the redirection themselves */
        if (isTotpLoading || isOtpEmailLoading || isSkipLoading) return;

        if (!isFeatureFlagsLoading && !hasZitadelMfaFlag) {
            /** Redirect to start of login flow if missing required feature flags */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        } else if (!loginInfo || !session || !loginInfo.isMfaRegistrationRequired) {
            /** Redirect to start of login flow if missing required variables */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        } else if (loginInfo.isTotpChallengeRequired) {
            /** Redirect to TOTP route when necessary */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(zitadelRoutes.TOTP_CHALLENGE.route));
        } else if (loginInfo.isOtpEmailChallengeRequired) {
            /** Redirect to OTP email route when necessary */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(zitadelRoutes.OTP_EMAIL_CHALLENGE.route));
        }
    }, [
        hasZitadelMfaFlag,
        isFeatureFlagsLoading,
        isOtpEmailLoading,
        isSkipLoading,
        isTotpLoading,
        loginInfo,
        navigate,
        session,
    ]);

    const isLoading = isFeatureFlagsLoading || isFallbackRedirectLoading;
    const isActionsDisabled = isTotpLoading || isOtpEmailLoading || isSkipLoading || isLoading;

    if (isLoading) return <Loading onTop overlay overlayColour={"white"} />;

    return (
        <div css={authContentStyle}>
            <div css={headerStyle}>
                <h1 css={leftHeaderStyle}>Multi-Factor Authentication</h1>
            </div>
            <div>
                Select one of the Multi-Factor methods below.
            </div>
            <div css={buttonSetStyle}>
                <Button
                    css={buttonStyle}
                    type="submit"
                    forceLoading={isTotpLoading}
                    disabled={isActionsDisabled}
                    onClick={onSelectTotp}
                    useOutline
                >
                    Use Authenticator App
                </Button>
                <Button
                    css={buttonStyle}
                    type="submit"
                    forceLoading={isOtpEmailLoading}
                    disabled={isActionsDisabled}
                    onClick={onSelectOtpEmail}
                    useOutline
                >
                    Get Code via Email
                </Button>
                <Button
                    css={cancelButtonStyle}
                    type="submit"
                    forceLoading={isSkipLoading}
                    disabled={isActionsDisabled}
                    onClick={onSelectSkip}
                    useOutline
                >
                    Skip
                </Button>
            </div>
        </div>
    );
};

const ZITADEL_START_TOTP_REGISTRATION = gql`
    mutation zitadelStartTotpRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelStartTotpRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            session {
                id
                token
                metadata {
                    userEmail
                }
            }
            secret {
                secret
                uri
            }
        }
    }
`;

const ZITADEL_SKIP_MFA_REGISTRATION = gql`
    mutation zitadelSkipMfaRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelSkipMfaRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
        }
    }
`;

// Copied from SignIn.tsx
const authContentStyle = (theme: EmotionTheme) => css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

// Copied from SignIn.tsx
const headerStyle = css`
    display: flex;
    justify-content: space-between;
`;

// Copied from SignIn.tsx
const leftHeaderStyle = (theme: EmotionTheme) => css`
    margin: 0;
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

// Copied from SignIn.tsx
const buttonStyle = (theme: EmotionTheme) => css`
    background: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;
const cancelButtonStyle = (theme: EmotionTheme) => css`
    ${buttonStyle(theme)};
    background: none;
    color: ${theme.colours.blue['400']};
    border: 1px solid ${theme.colours.blue['400']};
`;

const buttonSetStyle = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 20px 0;
`;

export default MfaInitialisation;
